import { Checkbox, CheckboxStates } from "@/components/ui/checkbox";
import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import SmartLabel from "@/domain/enums/SmartLabel";
import { cn } from "@/lib/utils";
import { CheckedState } from "@radix-ui/react-checkbox";
import { ReactNode, useEffect, useState } from "react";
import FocusSortToggle from "../toggles/FocusSortToggle";
import MailViewToggle, { MailView } from "../toggles/MailViewToggle";
import { MailMassAction } from "./MailAction";

interface EmailViewHeaderProps {
    selectedMails: Email[];
    mailsListFlattened: Email[];
    onSelectAllChange: (checked: boolean) => void;
    view: MailView;
    onViewChange: (view: MailView) => void;
    setSelectedMails: (selectedMails: Email[]) => void;
    title?: ReactNode;
    hideSideListMode?: boolean;
    listId: MailsListId;
    onFocusSortModeChange: () => void;
}

const EmailViewHeader = ({
    selectedMails,
    mailsListFlattened,
    onSelectAllChange,
    view,
    onViewChange,
    title,
    setSelectedMails,
    hideSideListMode,
    listId,
    onFocusSortModeChange,
}: EmailViewHeaderProps) => {
    const { isVistassistantResult } = useVistassistantMail();
    const [checkState, setCheckState] = useState<CheckedState>(
        Boolean(mailsListFlattened) &&
            selectedMails.length === mailsListFlattened?.length,
    );

    useEffect(() => {
        if (!mailsListFlattened) return;

        if (mailsListFlattened.length === selectedMails.length) {
            setCheckState(CheckboxStates.CHECKED);
        } else if (selectedMails.length !== 0) {
            setCheckState(CheckboxStates.INDETERMINATE);
        } else {
            setCheckState(CheckboxStates.UNCHECKED);
        }
    }, [selectedMails, mailsListFlattened]);

    const isSideList = view === MailView.SIDE_LIST;
    const hasSelection = Boolean(checkState) && mailsListFlattened.length > 0;

    const isFocus = listId === SmartLabel.Focus;

    return (
        <div
            className={cn(
                "relative py-2 pl-2 pr-3.5",
                isVistassistantResult && "pt-0",
            )}
        >
            {title}
            <div
                className={cn(
                    "flex flex-wrap items-center",
                    isSideList ? "gap-2" : "gap-x-4 gap-y-2",
                )}
            >
                <div
                    className={cn(
                        "flex shrink-0 items-center",
                        // Même hauteur que le sélecteur d'affichage, que les actions groupées remplacent pendant une sélection
                        isSideList && "h-[38px]",
                    )}
                >
                    <Checkbox
                        checked={checkState}
                        disabled={mailsListFlattened.length === 0}
                        onCheckedChange={() => {
                            if (mailsListFlattened.length === 0) {
                                return;
                            }

                            setCheckState(!checkState);
                            onSelectAllChange(!checkState);
                        }}
                    />
                </div>
                {hasSelection && (
                    <div className={isSideList ? "ml-auto" : "ml-1"}>
                        <MailMassAction
                            listId={listId}
                            selectedMails={selectedMails}
                            setSelectedMails={setSelectedMails}
                        />
                    </div>
                )}
                <div
                    className={
                        isSideList
                            ? "contents"
                            : "ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-2"
                    }
                >
                    {isFocus && (
                        <div
                            className={cn(
                                isSideList &&
                                    "order-first flex w-full justify-end",
                            )}
                        >
                            <div>
                                <FocusSortToggle
                                    compact={isSideList}
                                    onSortModeChange={onFocusSortModeChange}
                                />
                            </div>
                        </div>
                    )}
                    {!(isSideList && hasSelection) && (
                        <div className={cn(isSideList && "ml-auto")}>
                            <MailViewToggle
                                compact={isSideList}
                                hideSideListMode={hideSideListMode}
                                onChange={onViewChange}
                                view={view}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmailViewHeader;
